<template>
  <section>
    <div class="md:px-4 pt-2">
      <ul>
        <li
          class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 lg:py-6"
        >
          <span class="text-fg-muted">Currency</span>
          <span class="flex items-center gap-x-2">
            <currency-icons
              :currency="getCurrencyCountry(withdrawalCurrency)"
            />
            <span class="capitalize">
              {{ withdrawalCurrency }}
            </span>
          </span>
        </li>
        <li v-for="(detail, index) in withdrawalDetails" :key="index">
          <div
            v-if="detail.value"
            class="grid grid-cols-2 font-normal text-sm items-center border-b py-3.5 lg:py-6"
          >
            <span>{{ detail.title }}:</span>
            <span class="">
              {{ detail.value || '--' }}
              <span
                v-if="detail.copy"
                class="cursor-pointer mb-1 ml-2"
                v-clipboard:copy="detail.value"
                v-clipboard:success="onCopy"
                v-clipboard:error="onError"
              >
                <copy-icon />
              </span>
            </span>
          </div>
        </li>
        <li
          class="flex justify-between font-normal text-sm items-center rounded-md border-t py-3.5 lg:py-5"
          v-if="hasIntermediaryBank"
        >
          <span class="text-fg-muted">Intermediary Bank</span>
          <span
            class="text-primary font-semibold text-2xl cursor-pointer"
            @click="toggleShow('showIntermediaryBankDetails')"
            >{{ showIntermediaryBankDetails ? '-' : '+' }}</span
          >
        </li>
        <transition name="slide-fade">
          <div v-if="showIntermediaryBankDetails">
            <div
              v-for="(detail, index) in intermediaryBankDetails"
              :key="index"
              class="pt-3"
            >
              <div
                class="flex flex-row border rounded-md py-3.5 lg:py-5 bg-surface px-3 justify-between items-center mb-3"
              >
                <p class="text-sm font-thin text-fg-muted">
                  {{ detail.title || '--' }}
                </p>
                <p class="text-sm font-bold">
                  {{ detail.value || '--' }}
                  <span
                    v-if="detail.copy"
                    class="cursor-pointer mb-1 ml-2"
                    v-clipboard:copy="detail.value"
                    v-clipboard:success="onCopy"
                    v-clipboard:error="onError"
                  >
                    <copy-icon />
                  </span>
                </p>
              </div>
            </div>
          </div>
        </transition>
      </ul>
      <div
        @click="handleDeleteWithdrawalMethod"
        class="shadow-lg text-error-default bg-red-100 border-error-default my-4 py-3 px-3 font-normal w-11/12 text-center rounded-md cursor-pointer absolute bottom-1"
      >
        <span class="flex justify-center gap-x-3 items-center">
          <delete-icon class="h-5 w-5" />
          <span> Delete Account </span>
        </span>
      </div>
    </div>
  </section>
</template>

<script>
import breakpoints from '@/utils/breakpoints.js'
import { GetCurrencyCountry } from '@/utils/helpers.js'
export default {
  name: 'Drawer',
  data () {
    return {
      breakpoints,
      isLoading: false,
      showIntermediaryBankDetails: false
    }
  },
  props: {
    currentWithdrawalDetails: {
      type: Object,
      default: () => {},
      required: true
    }
  },
  components: {
    DeleteIcon: () => import('@/assets/icons/DeleteIcon.vue'),
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    CopyIcon: () => import('@/assets/icons/CopyIcon.vue'),
    Transition: () =>
      import('@/components/v2/animation/transition/Transition.vue')
  },
  computed: {
    drawerWidth () {
      return breakpoints.lgAndUp ? '35%' : breakpoints.mdAndUp ? '70%' : '90%'
    },
    withdrawalMethods () {
      return this.$store.state.withdrawal.withdrawalMethods
    },
    withdrawalCurrency () {
      return this.currentWithdrawalDetails?.currency
    },
    withdrawalDetails () {
      return [
        {
          title: 'Full Name',
          value:
            this.currentWithdrawalDetails?.details?.bankWithdrawalDetails
              ?.fullName ||
            this.currentWithdrawalDetails?.details?.bankDetails?.fullName,
          copy: false
        },
        {
          title: 'Country',
          value:
            this.currentWithdrawalDetails?.details?.bankWithdrawalDetails
              ?.country ||
            this.currentWithdrawalDetails?.details?.bankDetails?.country,
          copy: false
        },
        {
          title: 'Bank Name',
          value:
            this.currentWithdrawalDetails?.details?.bankWithdrawalDetails
              ?.bankName ||
            this.currentWithdrawalDetails?.details?.bankDetails?.bankName,
          copy: false
        },
        {
          title: 'Account Number',
          value:
            this.currentWithdrawalDetails?.details?.bankWithdrawalDetails
              ?.accountNumber ||
            this.currentWithdrawalDetails?.details?.bankDetails?.accountNumber,
          copy: true
        },
        {
          title: 'Routing Number',
          value:
            this.currentWithdrawalDetails?.details?.bankWithdrawalDetails
              ?.routingNumber ||
            this.currentWithdrawalDetails?.details?.bankDetails?.routingNumber,
          copy: true
        },
        {
          title: 'Bank Address',
          value:
            this.currentWithdrawalDetails?.details?.bankWithdrawalDetails
              ?.bankAddress ||
            this.currentWithdrawalDetails?.details?.bankDetails?.bankAddress,
          copy: false
        },
        {
          title: 'Postal Code',
          value:
            this.currentWithdrawalDetails?.details?.bankWithdrawalDetails
              ?.postalCode ||
            this.currentWithdrawalDetails?.details?.bankDetails?.postalCode,
          copy: true
        },
        {
          title: 'Swift Code',
          value:
            this.currentWithdrawalDetails?.details?.bankWithdrawalDetails
              ?.swiftCode ||
            this.currentWithdrawalDetails?.details?.bankDetails?.swiftCode,
          copy: false
        }
      ]
    },
    hasIntermediaryBank () {
      return this.currentWithdrawalDetails?.details?.intermediaryBank
    },
    intermediaryBankDetails () {
      const intermediaryBank =
        this.currentWithdrawalDetails?.details?.intermediaryBank
      const details = []

      if (intermediaryBank) {
        details.push({
          title: 'Bank Name',
          value: intermediaryBank.bankName || '--',
          copy: true,
          key: 'bankName'
        })
        details.push({
          title: 'Account Number',
          value: intermediaryBank.accountNumber || '--',
          copy: true,
          key: 'accountNumber'
        })
        details.push({
          title: 'Account Name',
          value: intermediaryBank.accountName || '--',
          copy: true,
          key: 'accountName'
        })
        details.push({
          title: 'Reference',
          value: intermediaryBank.reference || '--',
          copy: true,
          key: 'reference'
        })
      }

      return details
    }
  },
  methods: {
    toggleShow (arg) {
      this[arg] = !this[arg]
    },
    onCopy: function (e) {
      this.$notify.success({
        title: 'Copied'
      })
    },
    onError: function (e) {
      this.$notify.error('Error copying to clipboard!')
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    handleClose () {
      this.$emit('closeDrawer')
    },
    handleDeleteWithdrawalMethod () {
      this.$emit('handleDeleteWithdrawalMethod')
    }
  }
}
</script>

<style scoped></style>
